/**
 * BrandOverviewPage Component - FIXED: Removed infinite loop issues
 * Displays grid of all product brands with stable references and proper memoization
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useBrands } from '../../hooks/useProducts.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { imageHelpers } from '../../services/api.js';
// FIXED: Use the properly processed imageUrl from the service
const BrandCard = React.memo(({ brand }) => {
  console.log(brand);
  const brandImageUrls = imageHelpers.getBrandImageUrl(brand);

  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  
  const handleImageError = () => {
    if (currentUrlIndex < brandImageUrls.length - 1) {
      setCurrentUrlIndex(currentUrlIndex + 1);
    }
  };
  return (
    <Card className="h-100 border-0 shadow-sm brand-card">
      <div className="brand-card__image-wrapper">
       <img 
          src={brandImageUrls[currentUrlIndex]}
          alt={`${brand.name} logo`}
          className="brand-card__logo"
          onError={handleImageError}
          loading="lazy"
        />
      </div>
      
     
      <Card.Body className="text-center d-flex flex-column">
        <Card.Title className="brand-card__title">
          {brand.name}
        </Card.Title>
        
        {brand.description && (
          <Card.Text className="brand-card__description flex-grow-1">
            {brand.description.length > 100 
              ? `${brand.description.substring(0, 100)}...` 
              : brand.description
            }
          </Card.Text>
        )}
        
        {brand.productCount && (
          <Card.Text className="brand-card__count">
            {brand.productCount} products available
          </Card.Text>
        )}
        
        <Link 
          to={`/brands/${brand.slug}`}
          className="btn btn-primary brand-card__button mt-auto"
        >
          Shop {brand.name}
          <i className="bi bi-arrow-right ms-2"></i>
        </Link>
      </Card.Body>
    </Card>
  );
}, (prevProps, nextProps) => {
  // FIXED: Compare the properly processed imageUrl
  return prevProps.brand.id === nextProps.brand.id &&
         prevProps.brand.name === nextProps.brand.name &&
         prevProps.brand.imageUrl === nextProps.brand.imageUrl;
});

BrandCard.displayName = 'BrandCard';

const BrandOverviewPage = () => {
  // FIXED: Remove pageSize option that might be causing query instability
  const { data: brands = [], isLoading, error } = useBrands();

  // FIXED: Simple filter without complex memoization
  const validBrands = useMemo(() => {
    return Array.isArray(brands) ? brands.filter(brand => brand?.id && brand?.name) : [];
  }, [brands]);

  if (isLoading) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="py-5">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-3 text-muted">Loading brands...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="py-5">
          <div className="text-center">
            <div className="alert alert-danger">
              <h4>Error Loading Brands</h4>
              <p>{error.message || 'Failed to load brands'}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      {/* Header Section */}
      <section className="bg-white py-4 border-bottom px-3">
        <Container fluid className="">
          <Row>
            <Col>
              <div className="d-flex align-items-center justify-content-between flex-column flex-md-row">
                <div className="mb-4 mb-md-0 text-center text-md-start">
                  <h1 className="h2 mb-2">All Brands</h1>
                  <p className="text-muted mb-0">
                    Discover products from {validBrands.length} trusted brands
                  </p>
                </div>
                <div>
                  <Link to="/" className="btn btn-outline-primary">
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Home
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Brands Grid */}
      <section className="py-4 px-3">
        <Container fluid >
          {validBrands.length > 0 ? (
            <Row>
              {validBrands.map((brand) => (
                <Col key={brand.id} xl={3} lg={4} md={6} className="mb-4">
                  <BrandCard brand={brand} />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-tags display-1 text-muted mb-3"></i>
              <h3 className="text-muted">No Brands Available</h3>
              <p className="text-muted">Please contact support if you believe this is an error.</p>
              <Link to="/" className="btn btn-primary">
                Return to Home
              </Link>
            </div>
          )}
        </Container>
      </section>

      <style>{`
        .brand-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .brand-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }

        .brand-card__image-wrapper {
          background: #fff;
          overflow: hidden;
          border-bottom: 1px solid #e9ecef;
          aspect-ratio: 1 / 1;
        }

        .brand-card__logo {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform 0.3s ease;
        }

        .brand-card:hover .brand-card__logo {
          transform: scale(1.05);
        }

        .brand-card__title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #333;
          line-height: 1.2;
        }

        .brand-card__description {
          color: #6c757d;
          font-size: 0.9rem;
          margin-bottom: 15px;
          line-height: 1.4;
        }

        .brand-card__count {
          color: #6c757d;
          margin-bottom: 20px;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .brand-card__button {
          border-radius: 25px;
          padding: 10px 25px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }

        .brand-card__button:hover {
          transform: translateY(-1px);
        }

        /* Responsive adjustments */
        @media (max-width: 767.98px) {
          .brand-card__title {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default BrandOverviewPage;
